import React from "react"
import { toast } from "react-toastify"
import MyModal from "@common-utils/MyModal"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyGoogleDriveFileViewerInModal from "../../../../components/MyDocumentViewer/MyGoogleDriveFileViewerInModal"
import { IPersonnelDEDocumentsEmbauchePreviewModel, IPersonnelDocumentsEmbaucheFileModel } from "../../../personnel/core/_requests"
import { formatFileSize, MAX_EMAIL_ATTACHMENT_SIZE } from "../../../personnel/core/_download"
import { staffDocsEmbauchePreviewRequest, staffDocsEmbaucheEnvoyerRequest } from "./_requests"

type Props = {
     demandeId: number
     collaborateur: string
     onClose: () => void
     onSent: () => void
}

// Modale d'envoi des documents d'embauche depuis l'alerte staff (mêmes possibilités que la page dédiée : contrat + taille).
const StaffDocsEmbaucheSendModal = ({ demandeId, collaborateur, onClose, onSent }: Props) => {
     const [preview, setPreview] = React.useState<IPersonnelDEDocumentsEmbauchePreviewModel | null>(null)
     const [loading, setLoading] = React.useState(true)
     const [error, setError] = React.useState<string | null>(null)
     const [contratInclus, setContratInclus] = React.useState(true)
     const [sending, setSending] = React.useState(false)
     const [activeDoc, setActiveDoc] = React.useState<IPersonnelDocumentsEmbaucheFileModel | null>(null)

     React.useEffect(() => {
          staffDocsEmbauchePreviewRequest(demandeId)
               .then(r => setPreview(r.data))
               .catch(e => setError(e?.response?.data?.detail ?? "Impossible de charger les documents."))
               .finally(() => setLoading(false))
     }, [demandeId])

     const contratSelectionne = !!preview?.contratDisponible && contratInclus
     const nbDocs = (preview?.documentsEntite.length ?? 0) + (contratSelectionne ? 1 : 0)
     const maxEmailSize = preview?.maxEmailSize ?? MAX_EMAIL_ATTACHMENT_SIZE
     const totalSize = (preview?.documentsEntite.reduce((n, d) => n + (d.size ?? 0), 0) ?? 0) + (contratSelectionne ? preview?.contratSize ?? 0 : 0)
     const tooLarge = totalSize > maxEmailSize
     const noMail = !preview?.mailPersonnel

     function handleSend() {
          setSending(true)
          staffDocsEmbaucheEnvoyerRequest(demandeId, contratInclus)
               .then(() => {
                    toast.success("Les documents d'embauche ont été envoyés.")
                    onSent()
               })
               .catch(e => toast.error(e?.response?.data?.detail ?? "L'envoi a échoué.", { autoClose: false }))
               .finally(() => setSending(false))
     }

     return (
          <>
          <MyModal title={`Envoyer les documents d'embauche — ${collaborateur}`} show={true} size={"lg"} onCloseCallback={() => !sending && onClose()}>
               {loading ? (
                    <div className="d-flex justify-content-center py-10">
                         <MySimpleSpinner size={"md"} />
                    </div>
               ) : error ? (
                    <div className="alert alert-danger mb-0">{error}</div>
               ) : preview ? (
                    <div>
                         {/* Destinataire */}
                         <div className="d-flex flex-wrap gap-3 mb-5">
                              <div className="flex-grow-1 d-flex align-items-center gap-3 border border-dashed border-gray-300 rounded p-4 min-w-200px">
                                   <span className="symbol symbol-40px"><span className="symbol-label bg-light-primary text-primary"><i className="fas fa-building" /></span></span>
                                   <div className="min-w-0">
                                        <div className="text-muted fs-8 text-uppercase fw-bold">Entité &amp; statut</div>
                                        <div className="fw-bold fs-6 text-truncate">
                                             {preview.entite ?? "—"}
                                             {preview.statut && <span className="badge badge-light-info ms-2">{preview.statut}</span>}
                                        </div>
                                   </div>
                              </div>
                              <div className="flex-grow-1 d-flex align-items-center gap-3 border border-dashed border-gray-300 rounded p-4 min-w-200px">
                                   <span className="symbol symbol-40px"><span className={`symbol-label ${noMail ? "bg-light-danger text-danger" : "bg-light-success text-success"}`}><i className="fas fa-envelope" /></span></span>
                                   <div className="min-w-0">
                                        <div className="text-muted fs-8 text-uppercase fw-bold">Email personnel</div>
                                        <div className={`fw-bold fs-6 text-truncate ${noMail ? "text-danger" : ""}`}>{preview.mailPersonnel ?? "Non renseigné"}</div>
                                   </div>
                              </div>
                         </div>

                         {noMail && <div className="alert alert-warning">Aucune adresse email personnelle : impossible d'envoyer les documents.</div>}

                         {/* Contrat de travail : coché par défaut, décochable */}
                         <label
                              htmlFor="staff-alert-contrat"
                              className={`d-flex align-items-center gap-3 rounded p-4 mb-4 ${preview.contratDisponible ? "bg-light-primary cursor-pointer" : "bg-light-danger"}`}
                         >
                              <input
                                   id="staff-alert-contrat"
                                   className="form-check-input"
                                   type="checkbox"
                                   checked={contratSelectionne}
                                   disabled={!preview.contratDisponible}
                                   onChange={e => setContratInclus(e.target.checked)}
                              />
                              <i className="fas fa-file-signature fs-2 text-primary" />
                              <div>
                                   <div className="fw-bold text-gray-900">Contrat de travail</div>
                                   {preview.contratDisponible ? (
                                        <div className="text-muted fs-7">Inclus par défaut — décochez pour ne pas l'envoyer.</div>
                                   ) : (
                                        <div className="text-danger fs-7">Aucun contrat de travail disponible.</div>
                                   )}
                              </div>
                         </label>

                         {/* Documents de l'entité */}
                         <div className="fw-bold text-gray-800 mb-2">Documents du dossier de l'entité <span className="badge badge-light ms-1">{preview.documentsEntite.length}</span></div>
                         {preview.documentsEntite.length ? (
                              <div className="d-flex flex-column gap-2 mb-4">
                                   {preview.documentsEntite.map((doc, i) => (
                                        <div
                                             key={i}
                                             className="d-flex align-items-center gap-3 border border-dashed border-gray-300 rounded px-3 py-2 cursor-pointer bg-hover-light-primary"
                                             onClick={() => setActiveDoc(doc)}
                                             title="Cliquez pour visualiser"
                                        >
                                             <i className="fas fa-file-pdf fs-4 text-danger" />
                                             <span className="text-gray-800 fw-semibold text-truncate flex-grow-1">{doc.name ?? "Document"}</span>
                                             {doc.size != null && <span className="badge badge-light">{formatFileSize(doc.size)}</span>}
                                             <i className="fas fa-eye text-muted" />
                                        </div>
                                   ))}
                              </div>
                         ) : (
                              <div className="alert alert-warning">Aucun document d'embauche configuré pour cette entité et ce statut.</div>
                         )}

                         {/* Taille totale + garde e-mail */}
                         {nbDocs > 0 && (
                              <div className={`d-flex align-items-center justify-content-between p-3 rounded mb-2 ${tooLarge ? "bg-light-danger" : "bg-light"}`}>
                                   <span className="fw-semibold text-gray-800">Taille totale de l'envoi</span>
                                   <span className={`fw-bold ${tooLarge ? "text-danger" : "text-gray-800"}`}>{formatFileSize(totalSize)} / {formatFileSize(maxEmailSize)}</span>
                              </div>
                         )}
                         {tooLarge && <div className="alert alert-danger">Les documents dépassent la taille maximale pour un envoi par e-mail ({formatFileSize(maxEmailSize)}). Décochez le contrat ou réduisez la sélection.</div>}

                         <div className="d-flex justify-content-end gap-2 mt-6">
                              <button type="button" className="btn btn-sm btn-light" disabled={sending} onClick={onClose}>
                                   Annuler
                              </button>
                              <button
                                   type="button"
                                   className="btn btn-sm btn-success d-inline-flex align-items-center"
                                   disabled={sending || noMail || nbDocs === 0 || tooLarge}
                                   onClick={handleSend}
                              >
                                   {sending ? <MySimpleSpinner size={"sm"} /> : <><i className="fas fa-paper-plane me-1" /> Envoyer {nbDocs > 0 ? `(${nbDocs})` : ""}</>}
                              </button>
                         </div>
                    </div>
               ) : null}
          </MyModal>

          {activeDoc && (
               <MyGoogleDriveFileViewerInModal
                    googleDriveID={activeDoc.googleDriveID}
                    googleDriveMimetype={activeDoc.mimeType ?? ""}
                    modalTitle={<>{activeDoc.name ?? "Document"}</>}
                    handleCloseModal={() => setActiveDoc(null)}
               />
          )}
          </>
     )
}

export default StaffDocsEmbaucheSendModal
